import React, { useState } from 'react'
import { Heart, Users, Sparkles, Pencil, CheckCircle, Calendar } from 'lucide-react'
import { useDonation } from '../contexts/DonationContext'

// ============================================================================
// CAMPAIGN MODE TOGGLE
// 'active' = Active campaign with specific goal (e.g., Thanksgiving 2025)
// 'future' = Between campaigns - donations go toward future events
// ============================================================================
const CAMPAIGN_MODE: 'active' | 'future' = 'future'

interface DonationTier {
  tails: number
  amount: number
  label: string
  description: string
  icon: React.ReactNode
  popular?: boolean
}

/**
 * DONATION TIERS SELECTOR
 *
 * Allows donors to select from preset donation tiers or enter a custom amount.
 * Calculates and displays the number of lobster tails funded by each amount.
 */
const DonationTiers: React.FC = () => {
  const { amount, setAmount, getTailsFunded } = useDonation()
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState('')

  const COST_PER_TAIL = 8.40

  const tiers: DonationTier[] = [
    {
      tails: 1,
      amount: 8.40,
      label: '1 Tail',
      description: 'Feed one person',
      icon: <Heart className="w-8 h-8" />,
    },
    {
      tails: 5,
      amount: 42.00,
      label: '5 Tails',
      description: 'Feed a small group',
      icon: <Users className="w-8 h-8" />,
    },
    {
      tails: 10,
      amount: 84.00,
      label: '10 Tails',
      description: 'Feed a larger group',
      icon: <Users className="w-8 h-8" />,
      popular: true,
    },
    {
      tails: 25,
      amount: 210.00,
      label: '25 Tails',
      description: 'Make a major impact',
      icon: <Sparkles className="w-8 h-8" />,
    },
  ]

  const handleTierClick = (tierAmount: number) => {
    setAmount(tierAmount)
    setShowCustomInput(false)
    setCustomValue('')
  }

  const handleCustomClick = () => {
    setShowCustomInput(true)
    setCustomValue(amount > 0 ? amount.toFixed(2) : '')
  }

  const handleCustomSubmit = () => {
    const parsedAmount = parseFloat(customValue)
    if (!isNaN(parsedAmount) && parsedAmount >= 1.00 && parsedAmount <= 10000.00) {
      setAmount(parsedAmount)
      setShowCustomInput(false)
    }
  }

  const handleCancelCustom = () => {
    setShowCustomInput(false)
    setCustomValue('')
  }

  const handleClearAmount = () => {
    setAmount(0)
    setCustomValue('')
    setShowCustomInput(false)
  }

  // Check if a tier is selected
  const isTierSelected = (tierAmount: number) => {
    return Math.abs(amount - tierAmount) < 0.01 && !showCustomInput
  }

  // Check if custom amount is selected
  const isCustomSelected = () => {
    if (amount === 0) return false
    return !tiers.some(tier => isTierSelected(tier.amount)) || showCustomInput
  }

  // ============================================================================
  // FUTURE CAMPAIGNS UI
  // Shown when CAMPAIGN_MODE = 'future' - donations support upcoming events
  // ============================================================================
  if (CAMPAIGN_MODE === 'future') {
    return (
      <div className="mb-12">
        {/* Future Campaigns Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Support Future Campaigns
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Your donation helps us bring dignity and joy to more communities in need.
            Every <span className="font-semibold text-primary dark:text-[#DAA520]">${COST_PER_TAIL.toFixed(2)}</span> provides one gourmet lobster tail.
          </p>
        </div>

        {/* Preset Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {tiers.map((tier) => {
            const isSelected = isTierSelected(tier.amount)

            return (
              <button
                key={tier.amount}
                onClick={() => handleTierClick(tier.amount)}
                className={`relative flex flex-col items-center justify-center p-6 rounded-2xl border-3 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl ${
                  isSelected
                    ? 'border-[#8B3A3A] bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A] text-white shadow-2xl scale-105'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#8B3A3A] bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                }`}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-3 -right-3 bg-[#DAA520] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    POPULAR
                  </div>
                )}

                {/* Icon */}
                <div className={`mb-4 ${isSelected ? '!text-white' : 'text-[#8B3A3A] dark:text-[#DAA520]'}`}>
                  {tier.icon}
                </div>

                {/* Tail Count */}
                <div className={`text-4xl font-bold mb-2 ${isSelected ? '!text-[#DAA520]' : 'text-[#8B3A3A] dark:text-[#DAA520]'}`}>
                  {tier.tails}
                </div>

                {/* Label */}
                <div className={`text-xl font-semibold mb-2 ${isSelected ? '!text-[#DAA520]' : 'text-gray-900 dark:text-white'}`}>
                  {tier.label}
                </div>

                {/* Amount */}
                <div className={`text-2xl font-bold mb-3 ${isSelected ? '!text-white' : 'text-gray-900 dark:text-white'}`}>
                  ${tier.amount.toFixed(2)}
                </div>

                {/* Description */}
                <div className={`text-sm ${isSelected ? '!text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
                  {tier.description}
                </div>
              </button>
            )
          })}
        </div>

        {/* Custom Amount */}
        <div className="max-w-2xl mx-auto">
          {!showCustomInput ? (
            <button
              onClick={handleCustomClick}
              className={`w-full py-5 px-6 rounded-2xl border-3 transition-all duration-300 hover:shadow-xl ${
                isCustomSelected()
                  ? 'border-[#8B3A3A] bg-white dark:bg-gray-800 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#8B3A3A] bg-white dark:bg-gray-800'
              }`}
            >
              {isCustomSelected() ? (
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Custom Amount
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      ${amount.toFixed(2)}
                      {getTailsFunded(amount) >= 1 ? (
                        <span className="ml-2 text-lg font-semibold text-[#8B3A3A] dark:text-[#DAA520]">
                          ({getTailsFunded(amount)} {getTailsFunded(amount) === 1 ? 'tail' : 'tails'})
                        </span>
                      ) : (
                        <span className="ml-2 text-base font-medium text-[#8B3A3A] dark:text-[#DAA520]">
                          (Every dollar counts!)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-[#8B3A3A] dark:text-[#DAA520]">
                    <Pencil className="w-5 h-5" />
                    <span className="text-sm font-semibold">Edit</span>
                  </div>
                </div>
              ) : (
                <div className="text-xl font-semibold text-gray-900 dark:text-white">
                  Enter Custom Amount
                </div>
              )}
            </button>
          ) : (
            <div className="bg-white dark:bg-gray-800 border-3 border-[#8B3A3A] rounded-2xl p-6 shadow-xl">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Custom Donation Amount ($1.00 - $10,000.00)
              </label>
              <div className="grid grid-cols-2 gap-3 md:flex md:space-x-3">
                <div className="col-span-2 md:col-span-1 md:flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xl font-bold">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="1.00"
                    max="10000.00"
                    placeholder="0.00"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomSubmit()
                      }
                    }}
                    className="w-full pl-10 pr-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-[#8B3A3A] focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xl font-semibold"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleCustomSubmit}
                  disabled={!customValue || parseFloat(customValue) < 1.00}
                  className="col-span-1 py-4 bg-[#8B3A3A] text-white rounded-xl hover:bg-[#6B2A2A] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg md:px-8"
                >
                  Set Amount
                </button>
                <button
                  onClick={handleCancelCustom}
                  className="col-span-1 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 font-semibold text-base md:text-lg md:px-6"
                >
                  Cancel
                </button>
                {amount > 0 && (
                  <button
                    onClick={handleClearAmount}
                    className="col-span-2 md:col-span-1 py-4 border-2 border-red-300 dark:border-red-600 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 font-semibold text-base md:text-lg md:px-6"
                  >
                    Clear
                  </button>
                )}
              </div>
              {customValue && parseFloat(customValue) >= 1.00 && (
                <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                  {getTailsFunded(parseFloat(customValue)) >= 1 ? (
                    <>
                      This will fund <span className="font-bold text-[#8B3A3A] dark:text-[#DAA520] text-lg">{getTailsFunded(parseFloat(customValue))}</span> lobster {getTailsFunded(parseFloat(customValue)) === 1 ? 'tail' : 'tails'}
                    </>
                  ) : (
                    <span className="text-[#8B3A3A] dark:text-[#DAA520]">
                      Every dollar counts! Your contribution helps us reach our goal.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Minimum donation notice */}
          <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
            100% of donations go toward feeding people in need at future events.
          </p>
        </div>
      </div>
    )
  }

  // ============================================================================
  // ACTIVE CAMPAIGN UI (Original)
  // Shown when CAMPAIGN_MODE = 'active' - specific campaign with goal
  // ============================================================================
  return (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          Choose Your Impact
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Every <span className="font-semibold text-primary dark:text-yellow-400">${COST_PER_TAIL.toFixed(2)}</span> provides one gourmet lobster tail to someone in need
        </p>
      </div>

      {/* Preset Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {tiers.map((tier) => {
          const isSelected = isTierSelected(tier.amount)

          return (
            <button
              key={tier.amount}
              onClick={() => handleTierClick(tier.amount)}
              className={`relative flex flex-col items-center justify-center p-6 rounded-2xl border-3 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl ${
                isSelected
                  ? 'border-[#8B3A3A] bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A] text-white shadow-2xl scale-105'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#8B3A3A] bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
              }`}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-3 -right-3 bg-[#DAA520] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  POPULAR
                </div>
              )}

              {/* Icon */}
              <div className={`mb-4 ${isSelected ? '!text-white' : 'text-[#8B3A3A] dark:text-[#DAA520]'}`}>
                {tier.icon}
              </div>

              {/* Tail Count */}
              <div className={`text-4xl font-bold mb-2 ${isSelected ? '!text-[#DAA520]' : 'text-[#8B3A3A] dark:text-[#DAA520]'}`}>
                {tier.tails}
              </div>

              {/* Label */}
              <div className={`text-xl font-semibold mb-2 ${isSelected ? '!text-[#DAA520]' : 'text-gray-900 dark:text-white'}`}>
                {tier.label}
              </div>

              {/* Amount */}
              <div className={`text-2xl font-bold mb-3 ${isSelected ? '!text-white' : 'text-gray-900 dark:text-white'}`}>
                ${tier.amount.toFixed(2)}
              </div>

              {/* Description */}
              <div className={`text-sm ${isSelected ? '!text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
                {tier.description}
              </div>
            </button>
          )
        })}
      </div>

      {/* Custom Amount */}
      <div className="max-w-2xl mx-auto">
        {!showCustomInput ? (
          <button
            onClick={handleCustomClick}
            className={`w-full py-5 px-6 rounded-2xl border-3 transition-all duration-300 hover:shadow-xl ${
              isCustomSelected()
                ? 'border-[#8B3A3A] bg-white dark:bg-gray-800 shadow-lg'
                : 'border-gray-200 dark:border-gray-700 hover:border-[#8B3A3A] bg-white dark:bg-gray-800'
            }`}
          >
            {isCustomSelected() ? (
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Custom Amount
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${amount.toFixed(2)}
                    {getTailsFunded(amount) >= 1 ? (
                      <span className="ml-2 text-lg font-semibold text-[#8B3A3A]">
                        ({getTailsFunded(amount)} {getTailsFunded(amount) === 1 ? 'tail' : 'tails'})
                      </span>
                    ) : (
                      <span className="ml-2 text-base font-medium text-[#8B3A3A]">
                        (Every dollar counts!)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-[#8B3A3A]">
                  <Pencil className="w-5 h-5" />
                  <span className="text-sm font-semibold">Edit</span>
                </div>
              </div>
            ) : (
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                Enter Custom Amount
              </div>
            )}
          </button>
        ) : (
          <div className="bg-white dark:bg-gray-800 border-3 border-[#8B3A3A] rounded-2xl p-6 shadow-xl">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Custom Donation Amount ($1.00 - $10,000.00)
            </label>
            {/* Mobile: 2-column grid, Desktop: flex row */}
            <div className="grid grid-cols-2 gap-3 md:flex md:space-x-3">
              {/* Input spans full width on mobile, flex-1 on desktop */}
              <div className="col-span-2 md:col-span-1 md:flex-1 relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xl font-bold">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="1.00"
                  max="10000.00"
                  placeholder="0.00"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomSubmit()
                    }
                  }}
                  className="w-full pl-10 pr-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-[#8B3A3A] focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xl font-semibold"
                  autoFocus
                />
              </div>
              {/* Set Amount button */}
              <button
                onClick={handleCustomSubmit}
                disabled={!customValue || parseFloat(customValue) < 1.00}
                className="col-span-1 py-4 bg-[#8B3A3A] text-white rounded-xl hover:bg-[#6B2A2A] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg md:px-8"
              >
                Set Amount
              </button>
              {/* Cancel button */}
              <button
                onClick={handleCancelCustom}
                className="col-span-1 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 font-semibold text-base md:text-lg md:px-6"
              >
                Cancel
              </button>
              {/* Clear button spans full width on mobile */}
              {amount > 0 && (
                <button
                  onClick={handleClearAmount}
                  className="col-span-2 md:col-span-1 py-4 border-2 border-red-300 dark:border-red-600 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 font-semibold text-base md:text-lg md:px-6"
                >
                  Clear
                </button>
              )}
            </div>
            {customValue && parseFloat(customValue) >= 1.00 && (
              <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                {getTailsFunded(parseFloat(customValue)) >= 1 ? (
                  <>
                    This will fund <span className="font-bold text-[#8B3A3A] text-lg">{getTailsFunded(parseFloat(customValue))}</span> lobster {getTailsFunded(parseFloat(customValue)) === 1 ? 'tail' : 'tails'}
                  </>
                ) : (
                  <span className="text-[#8B3A3A]">
                    Every dollar counts! Your contribution helps us reach our goal.
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Minimum donation notice */}
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
          Minimum donation is $1. We appreciate your contribution — every dollar counts!
        </p>
      </div>
    </div>
  )
}

export default DonationTiers
